import { useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import { ThemeToggle } from '@/components/ThemeToggle'
import { usePostApiCompanymanagementuser } from '@/api/generated/company-management-user/company-management-user'
import { useAuthStore } from '@/store/auth.store'
import { isServerUnreachable } from '@/lib/apiError'
import { Button } from '@/components/shadcn/button'
import { Input } from '@/components/shadcn/input'
import { Label } from '@/components/shadcn/label'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/shadcn/card'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/shadcn/popover'
import { Eye, EyeOff, Info, Check, X } from 'lucide-react'
import { WallpaperBackground } from '@/pages/login/components/WallpaperBackground'
import { PASSWORD_RULES, isPasswordValid } from '@/pages/login/passwordRules'

interface FormData {
  firstName: string
  lastName: string
  email: string
  emailRepeat: string
  password: string
  passwordRepeat: string
  companyName: string
  street: string
  streetNumber: string
  postalCode: string
  town: string
  country: string
  extraAddressLine: string
  phoneNumber: string
  taxId: string
  invoiceEmail: string
}

const INITIAL: FormData = {
  firstName: '',
  lastName: '',
  email: '',
  emailRepeat: '',
  password: '',
  passwordRepeat: '',
  companyName: '',
  street: '',
  streetNumber: '',
  postalCode: '',
  town: '',
  country: '',
  extraAddressLine: '',
  phoneNumber: '',
  taxId: '',
  invoiceEmail: '',
}

function getPasswordRules(t: TFunction) {
  return PASSWORD_RULES.map((rule) => ({ label: t(rule.labelKey), test: rule.test }))
}

function FieldLabel({ htmlFor, children, required = false }: { htmlFor: string; children: React.ReactNode; required?: boolean }) {
  return (
    <Label htmlFor={htmlFor} className="flex items-center gap-1">
      {children}
      {required && <span className="text-destructive">*</span>}
    </Label>
  )
}

export default function RegisterPage() {
  const { t } = useTranslation('login')
  const navigate = useNavigate()
  const setAuth = useAuthStore((s) => s.setAuth)

  const [step, setStep] = useState(1)
  const [form, setForm] = useState<FormData>(INITIAL)
  const [showPassword, setShowPassword] = useState(false)
  const [showPasswordRepeat, setShowPasswordRepeat] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { mutate: register, isPending } = usePostApiCompanymanagementuser({
    mutation: {
      onSuccess: (response) => {
        setAuth(response.data)
        navigate('/', { replace: true })
      },
      onError: (error) => {
        setError(isServerUnreachable(error) ? t('serverUnreachable') : t('registerPage.errors.registrationFailed'))
      },
    },
  })

  const set = (field: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }))

  const passwordRules = getPasswordRules(t)
  const passwordValid = isPasswordValid(form.password)

  const handleNext = (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.firstName || !form.lastName || !form.email || !form.emailRepeat || !form.password || !form.passwordRepeat) {
      setError(t('registerPage.errors.requiredFields'))
      return
    }
    if (!passwordValid) {
      setError(t('registerPage.errors.passwordInvalid'))
      return
    }
    if (form.email !== form.emailRepeat) {
      setError(t('registerPage.errors.emailMismatch'))
      return
    }
    if (form.password !== form.passwordRepeat) {
      setError(t('registerPage.errors.passwordMismatch'))
      return
    }
    setError(null)
    setStep(2)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.companyName || !form.street || !form.streetNumber || !form.postalCode || !form.town || !form.country || !form.invoiceEmail) {
      setError(t('registerPage.errors.requiredFields'))
      return
    }
    setError(null)
    register({
      data: {
        email: form.email,
        password: form.password,
        firstName: form.firstName,
        lastName: form.lastName,
        company: {
          name: form.companyName,
          invoiceEmails: form.invoiceEmail ? [form.invoiceEmail] : [],
          phoneNumber: form.phoneNumber || null,
          taxId: form.taxId || null,
          address: {
            street: form.street,
            number: form.streetNumber,
            postalCode: form.postalCode,
            town: form.town,
            country: form.country,
            extraAddressLine: form.extraAddressLine || null,
          },
        },
      },
    })
  }

  function renderStep1() {
    return (
      <form onSubmit={handleNext} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <FieldLabel htmlFor="firstName">{t('registerPage.firstName')}</FieldLabel>
            <Input id="firstName" placeholder={t('registerPage.placeholders.firstName')} required value={form.firstName} onChange={set('firstName')} />
          </div>
          <div className="space-y-2">
            <FieldLabel htmlFor="lastName">{t('registerPage.lastName')}</FieldLabel>
            <Input id="lastName" placeholder={t('registerPage.placeholders.lastName')} required value={form.lastName} onChange={set('lastName')} />
          </div>
        </div>

        <div className="space-y-2">
          <FieldLabel htmlFor="email">{t('registerPage.email')}</FieldLabel>
          <Input id="email" type="email" placeholder={t('registerPage.placeholders.email')} required value={form.email} onChange={set('email')} autoComplete="email" />
        </div>

        <div className="space-y-2">
          <FieldLabel htmlFor="emailRepeat">{t('registerPage.emailRepeat')}</FieldLabel>
          <Input id="emailRepeat" type="email" placeholder={t('registerPage.placeholders.email')} required value={form.emailRepeat} onChange={set('emailRepeat')} />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <FieldLabel htmlFor="password">{t('registerPage.password')}</FieldLabel>
            <Popover>
              <PopoverTrigger asChild>
                <button type="button" className={passwordValid ? 'text-green-500' : form.password ? 'text-destructive' : 'text-muted-foreground'}>
                  <Info className="h-4 w-4" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-3" align="end">
                <p className="text-xs font-medium mb-2">{t('registerPage.passwordRequirements')}</p>
                <ul className="space-y-1">
                  {passwordRules.map((rule) => {
                    const ok = rule.test(form.password)
                    return (
                      <li key={rule.label} className="flex items-center gap-2 text-xs">
                        {ok
                          ? <Check className="h-3 w-3 text-green-500 shrink-0" />
                          : <X className="h-3 w-3 text-destructive shrink-0" />
                        }
                        <span className={ok ? 'text-green-600' : 'text-muted-foreground'}>
                          {rule.label}
                        </span>
                      </li>
                    )
                  })}
                </ul>
              </PopoverContent>
            </Popover>
          </div>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              required
              value={form.password}
              onChange={set('password')}
              autoComplete="new-password"
              className="pr-10"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <div className="space-y-2">
          <FieldLabel htmlFor="passwordRepeat">{t('registerPage.passwordRepeat')}</FieldLabel>
          <div className="relative">
            <Input
              id="passwordRepeat"
              type={showPasswordRepeat ? 'text' : 'password'}
              required
              value={form.passwordRepeat}
              onChange={set('passwordRepeat')}
              className="pr-10"
            />
            <button
              type="button"
              onClick={() => setShowPasswordRepeat(!showPasswordRepeat)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              {showPasswordRepeat ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <Button type="submit" className="w-full">{t('registerPage.next')}</Button>
      </form>
    )
  }

  function renderStep2() {
    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <FieldLabel htmlFor="companyName" required>{t('registerPage.companyName')}</FieldLabel>
          <Input id="companyName" placeholder={t('registerPage.placeholders.companyName')} required value={form.companyName} onChange={set('companyName')} />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2 space-y-2">
            <FieldLabel htmlFor="street" required>{t('registerPage.street')}</FieldLabel>
            <Input id="street" placeholder={t('registerPage.placeholders.street')} required value={form.street} onChange={set('street')} />
          </div>
          <div className="space-y-2">
            <FieldLabel htmlFor="streetNumber" required>{t('registerPage.streetNumber')}</FieldLabel>
            <Input id="streetNumber" placeholder={t('registerPage.placeholders.streetNumber')} required value={form.streetNumber} onChange={set('streetNumber')} />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <FieldLabel htmlFor="postalCode" required>{t('registerPage.postalCode')}</FieldLabel>
            <Input id="postalCode" placeholder={t('registerPage.placeholders.postalCode')} required value={form.postalCode} onChange={set('postalCode')} />
          </div>
          <div className="space-y-2">
            <FieldLabel htmlFor="town" required>{t('registerPage.town')}</FieldLabel>
            <Input id="town" placeholder={t('registerPage.placeholders.town')} required value={form.town} onChange={set('town')} />
          </div>
        </div>

        <div className="space-y-2">
          <FieldLabel htmlFor="country" required>{t('registerPage.country')}</FieldLabel>
          <Input id="country" placeholder={t('registerPage.placeholders.country')} required value={form.country} onChange={set('country')} />
        </div>

        <div className="space-y-2">
          <FieldLabel htmlFor="invoiceEmail" required>{t('registerPage.invoiceEmail')}</FieldLabel>
          <Input id="invoiceEmail" type="email" placeholder={t('registerPage.placeholders.invoiceEmail')} required value={form.invoiceEmail} onChange={set('invoiceEmail')} />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <FieldLabel htmlFor="phoneNumber">{t('registerPage.phoneNumber')}</FieldLabel>
            <Input id="phoneNumber" placeholder={t('registerPage.placeholders.phoneNumber')} value={form.phoneNumber} onChange={set('phoneNumber')} />
          </div>
          <div className="space-y-2">
            <FieldLabel htmlFor="taxId">{t('registerPage.taxId')}</FieldLabel>
            <Input id="taxId" placeholder={t('registerPage.placeholders.taxId')} value={form.taxId} onChange={set('taxId')} />
          </div>
        </div>

        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => { setError(null); setStep(1) }}
            className="flex-none px-4"
          >
            {t('registerPage.back')}
          </Button>
          <Button type="submit" disabled={isPending} className="flex-1">
            {isPending ? t('registerPage.submitting') : t('registerPage.submit')}
          </Button>
        </div>
      </form>
    )
  }

  return (
    <WallpaperBackground>
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                <span>{t('registerPage.step', { step })}</span>
                <div className="h-1 flex-1 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full transition-all duration-300"
                    style={{ width: step === 1 ? '50%' : '100%' }}
                  />
                </div>
              </div>
              <CardTitle className="text-2xl">
                {step === 1 ? t('registerPage.titleStep1') : t('registerPage.titleStep2')}
              </CardTitle>
              <CardDescription>
                {step === 1
                  ? t('registerPage.descriptionStep1')
                  : t('registerPage.descriptionStep2')}
              </CardDescription>
            </div>
            <div className="flex items-center gap-1">
              <ThemeToggle />
              <LanguageSwitcher />
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {step === 1 ? renderStep1() : renderStep2()}

          {error && (
            <p className="text-sm text-destructive mt-4">{error}</p>
          )}

          <div className="mt-6 pt-6 border-t border-border flex items-center justify-between text-sm">
            <span className="text-muted-foreground">{t('registerPage.alreadyRegistered')}</span>
            <Link to="/login" className="font-medium text-primary hover:underline">{t('registerPage.login')}</Link>
          </div>
        </CardContent>
      </Card>
    </WallpaperBackground>
  )
}
